import React, { useState, useEffect } from "react";
import T from '../../components/common/T';
import { useNavigate, useParams } from "react-router-dom";
import { useAssets } from "../../context/AssetContext";
import { useProjects } from "../../context/ProjectContext";
import { toast } from "react-toastify";
import { WrenchScrewdriverIcon, ArrowLeftIcon, ExclamationCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldRenderer from '../../components/common/CustomFieldRenderer';

const AssetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addAsset, updateAsset, getAssetById } = useAssets();
  const { projects } = useProjects();
  const isEditing = Boolean(id);

  const [isDirty, setIsDirty] = useState(false);

  const { customData, setCustomData, saveCustomFields } = useCustomFields('assets', id ?? null);
  
  useUnsavedChanges(isDirty);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    category: "Heavy Machinery",
    status: "Available",
    current_project_id: "",
    location: "",
    cost_rate: "",
    purchase_date: "",
    notes: "",
    estimated_hours: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loadedIdRef = React.useRef(null);

  useEffect(() => {
    if (isEditing && id) {
      if (loadedIdRef.current === String(id)) return;
      loadedIdRef.current = String(id);
      loadAssetData();
    }
  }, [id, isEditing]);

  const loadAssetData = async () => {
    setIsLoading(true);
    try {
      const data = await getAssetById(id);
      if (data) {
        setFormData({
          name: data.name || "",
          code: data.code || "",
          category: data.category || "Heavy Machinery",
          status: data.status || "Available",
          current_project_id: data.current_project_id || "",
          location: data.location || "",
          cost_rate: data.cost_rate || "",
          purchase_date: data.purchase_date ? String(data.purchase_date).split('T')[0] : "",
          notes: data.notes || "",
          estimated_hours: ""
        });
      } else {
        toast.error("Asset not found");
        navigate("/admin/assets");
      }
    } catch (err) {
      toast.error("Could not load asset details");
      navigate("/admin/assets");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        cost_rate: formData.cost_rate ? parseFloat(formData.cost_rate) : 0,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : 0,
        current_project_id: formData.current_project_id || null,
        custom_data: customData // 🟢 ENSURE CUSTOM DATA IS IN PAYLOAD
      };

      if (isEditing) {
        const success = await updateAsset(id, submitData);
        if (success) {
          await saveCustomFields(id);
          setIsDirty(false);
          toast.success("Asset updated!");
          setTimeout(() => navigate("/admin/assets"), 800);
        }
      } else {
        const newAsset = await addAsset(submitData);
        if (newAsset) {
          const newId = newAsset?.id || newAsset?.data?.id;
          if (newId) await saveCustomFields(newId);
          setIsDirty(false);
          if (submitData.estimated_hours > 0) {
            toast.success("Cost added to Budget successfully!");
          }
          setTimeout(() => navigate("/admin/assets"), 800);
        }
      }
    } catch (err) {
      toast.error("An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <div className="p-10 text-center">Loading...</div>;

  const costPreview = (parseFloat(formData.cost_rate) || 0) * (parseFloat(formData.estimated_hours) || 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen flex justify-center">
      <div className="w-full max-w-4xl bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-8 py-6 text-white flex items-center gap-4">
          <button onClick={() => navigate("/admin/assets")} className="hover:text-gray-300">
            <ArrowLeftIcon className="w-6 h-6"/>
          </button>
          <div>
            <h1 className="text-base font-bold">{isEditing ? "Edit Asset" : "Add New Asset"}</h1>
            <p className="text-gray-400 text-sm">Manage asset details and project assignment</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Asset Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange}
                className="w-full border p-3 rounded-lg focus:ring-purple-500" required />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Serial / Code</label>
              <input type="text" name="code" value={formData.code} onChange={handleChange}
                className="w-full border p-3 rounded-lg focus:ring-purple-500" required />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
               <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
               <select name="category" value={formData.category} onChange={handleChange} className="w-full border p-3 rounded-lg">
                 <option value="Heavy Machinery">Heavy Machinery</option>
                 <option value="Vehicle">Vehicle</option>
                 <option value="Tool">Tool</option>
               </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Cost Rate (₹/Hour)</label>
              <input type="number" name="cost_rate" value={formData.cost_rate} onChange={handleChange}
                className="w-full border p-3 rounded-lg" placeholder="0.00" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Location</label>
              <input type="text" name="location" value={formData.location} onChange={handleChange}
                className="w-full border p-3 rounded-lg" />
            </div>
          </div>

          {/* Project Assignment Section */}
          <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
            <h3 className="text-base font-bold text-blue-900 uppercase mb-4">Project Assignment</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Assign to Project</label>
                <select name="current_project_id" value={formData.current_project_id} onChange={handleChange}
                  className="w-full border border-gray-300 p-3 rounded-lg bg-white">
                  <option value="">-- Unassigned --</option>
                  {projects?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Current Status</label>
                <select name="status" value={formData.status} onChange={handleChange}
                   className="w-full border border-gray-300 p-3 rounded-lg bg-white">
                  <option value="Available">Available</option>
                  <option value="In Use">In Use</option>
                  <option value="Under Maintenance">Under Maintenance</option>
                  <option value="Retired">Retired</option>
                </select>
              </div>
            </div>

            {/* Log Usage / Cost */}
            {formData.current_project_id && (
              <div className="mt-6 pt-6 border-t border-blue-200">
                <div className="flex items-start gap-4">
                   <div className="bg-white p-2 rounded-lg border border-blue-200 text-blue-600">
                      <ClockIcon className="w-6 h-6" />
                   </div>
                   <div className="flex-1">
                      <label className="block text-sm font-bold text-gray-800 mb-1">
                        Log Initial Usage / Cost <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Enter hours to immediately add an <b>Equipment Cost</b> entry to the Project Budget.
                      </p>
                      <div className="flex gap-4 items-center">
                        <input 
                          type="number" 
                          name="estimated_hours" 
                          value={formData.estimated_hours} 
                          onChange={handleChange}
                          className="w-32 border border-blue-300 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-800"
                          placeholder="Hours"
                          min="0"
                        />
                        <span className="text-gray-500 text-sm">
                          x ₹{parseFloat(formData.cost_rate || 0).toLocaleString('en-IN')}/hr =
                        </span>
                        <span className="text-lg font-bold text-green-600">
                           {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(costPreview)}
                        </span>
                      </div>
                   </div>
                </div>
              </div>
            )}
          </div>

          {/* 🟢 Custom Fields */}
          <CustomFieldRenderer 
             module="assets" 
             customData={customData} 
             onChange={(newData) => {
                 setCustomData(newData);
                 setIsDirty(true);
             }} 
          />

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => navigate("/admin/assets")} className="px-6 py-3 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-6 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 shadow-lg">
              {isSubmitting ? "Saving..." : "Save Asset"}
            </button>
          </div>
        </form>
      </div>

      <NavigationBlocker isDirty={isDirty} />
    </div>
  );
};

export default AssetForm;